import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

// Single source of truth for contact avatars across Ubik: CIRCULAR, with an
// initials fallback, an optional photo, and an optional colour-seed background
// for visual grouping (used by MeetingsList).
//
// It defaulted to square, to match "the rest of the UI's boxy language". That
// language is gone — the boxy look was the old design system, and a square
// avatar beside a round status dot was one of the clearer tells. shadcn's
// Avatar is round; so is this.
//
// Sizes map roughly to existing call-sites:
//   xs (h-5 w-5, 10px text)  — inline sender chip in inbox row
//   sm (h-7 w-7, 11px text)  — list rows in pickers
//   md (h-9 w-9, 13px text)  — task / approval rows
//   lg (h-11 w-11, 15px text) — detail-view contact header

type AvatarSize = "xs" | "sm" | "md" | "lg";

const sizeClasses: Record<AvatarSize, string> = {
  xs: "h-5 w-5 text-xs",
  sm: "h-7 w-7 text-xs",
  md: "h-9 w-9 text-row",
  lg: "h-11 w-11 text-base",
};

function initials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  // One word, one letter. Two letters from a single word reads as a
  // truncation bug — "You" became "YO" when the task rows moved onto this
  // component. The length-based rule I tried first was worse: it made "Sai"
  // and "Sarah" disagree for no reason a reader could see.
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Stable HSL hue derived from a seed string — used when the caller wants
// background-colour grouping (e.g. meetings by customer).
function hueFromSeed(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) & 0xffffffff;
  return Math.abs(hash) % 360;
}

export function ContactAvatar({
  name,
  imageSrc,
  size = "sm",
  colorSeed,
  shape = "circle",
  className,
}: {
  name: string;
  imageSrc?: string;
  size?: AvatarSize;
  /** When set, the fallback gets a tinted background derived from this seed. */
  colorSeed?: string;
  /**
   * Avatar shape. Circular by default. `"square"` is kept for a surface that
   * genuinely needs to read as a tile rather than a person — an app or
   * organisation mark, not a face.
   */
  shape?: "square" | "circle";
  className?: string;
}) {
  // Radix owns the fallback chain now. This used to be a `useState` for
  // `imageFailed` plus a `useEffect` to reset it when `imageSrc` changed, an
  // `onError` on a raw <img>, and a ternary — a hand-rolled version of
  // exactly what AvatarImage/AvatarFallback already do, and worse: Radix also
  // distinguishes "loading" from "failed", so the initials no longer flash
  // before a photo that was going to load fine.
  //
  // `avatar.tsx`'s own docstring already claimed "ContactAvatar ... now
  // renders through this". It did not. That is the second docstring in this
  // repo found asserting work nobody did, after progress.tsx's "replaces ~24
  // hand-rolled bars" when it had replaced 8.
  const showColorBg = !imageSrc && colorSeed;
  const shapeClass = shape === "circle" ? "rounded-full" : "rounded-md";
  // ONE seeded treatment, not two. A circular seeded avatar used to get a
  // saturated `hsl(h 65% 45%)` fill with white initials, and that fails WCAG
  // badly at some hues: the seed varies the HUE while lightness stays fixed,
  // but perceived luminance swings enormously across hues at one lightness.
  // White on the yellow end measured 2.01:1, and the harness caught a live
  // one at 2.34:1. No single lightness fixes it — even dropping to 30% only
  // reaches 4.30:1, and by then the colours are too muddy to tell apart,
  // which is the entire point of seeding them.
  //
  // The pale treatment this component already used for square avatars does
  // work, because both fill and text are derived from the same hue at
  // lightnesses far enough apart: 4.76:1 at its worst hue (60), 8.08:1 at its
  // best, checked across all 360. So circles take it too, and get the
  // hairline back — the saturated fill used to be the boundary, and a pale
  // one is not.
  const borderClass = "border border-border";
  const style = showColorBg
    ? { backgroundColor: `hsl(${hueFromSeed(colorSeed)} 70% 88%)`, color: `hsl(${hueFromSeed(colorSeed)} 45% 30%)` }
    : undefined;

  return (
    <Avatar
      className={cn("shrink-0", borderClass, shapeClass, sizeClasses[size], className)}
      aria-hidden="true"
    >
      {imageSrc ? <AvatarImage src={imageSrc} alt="" className="object-cover" /> : null}
      <AvatarFallback
        className={cn(
          "font-semibold uppercase tracking-wide",
          shapeClass,
          // The size class carries the type scale; without it the fallback
          // keeps AvatarFallback's own text-xs at every size.
          sizeClasses[size],
          showColorBg ? "" : "bg-background text-foreground/65",
        )}
        style={style}
      >
        {initials(name)}
      </AvatarFallback>
    </Avatar>
  );
}
